import React from 'react';
import { isEqual } from 'lodash';
import { Stack, Typography } from '@mui/material';
import { ArrowRightAlt } from '@mui/icons-material';

import { NamedEntityList } from './NamedEntityList';
import { ProductCatalogDocumentsChanges, ScalarChange, NamedEntity } from '../../../../types';
import { ReadOnlyQuill } from '../../../../../../desking/ui/components/catalogDetails/ReadOnlyQuill';
import { RenderWithCondition } from '../../../../../../../components/shared/Conditional/RenderWithCondition';

const LABELS: Record<keyof ProductCatalogDocumentsChanges, string> = {
  addonIds: 'Add-ons',
  document: 'Document',
  productSummary: 'Product Summary',
  legalDisclaimer: 'Legal Disclaimer',
  coverageDetails: 'Coverage Details',
  paragraphDescription: 'Paragraph Description',
  limitationsOfLiability: 'Limitations of Liability',
  eligibilityAndInclusions: 'Eligibility & Inclusions',
};

const EMPTY_QUILL = { ops: [{ insert: '\n' }] };

interface ProductCatalogDocumentsRenderProps {
  changes: ProductCatalogDocumentsChanges;
}

export const ProductCatalogDocumentsRender = ({ changes }: ProductCatalogDocumentsRenderProps) => (
  <Stack gap="0.5rem">
    {(Object.keys(changes) as Array<keyof ProductCatalogDocumentsChanges>).map(key => {
      const change = changes[key];

      if (!change) return null;

      const label = LABELS[key] ?? key;

      if (key === 'addonIds') {
        const addonChange = change as ScalarChange<NamedEntity[]>;

        return (
          <Stack direction="row" gap="0.5rem" alignItems="center" flexWrap="wrap">
            <Typography>{label}:</Typography>

            <Stack direction="row" gap="0.5rem" alignItems="center">
              <NamedEntityList items={addonChange.before} />

              <ArrowRightAlt />

              <NamedEntityList items={addonChange.after} />
            </Stack>
          </Stack>
        );
      }

      if (key === 'document') {
        const documentChange = change as ScalarChange<{ docName: string; documentId: string }>;

        return (
          <Stack direction="row" gap="0.5rem" alignItems="center" flexWrap="wrap">
            <Typography>{label}:</Typography>

            <Stack direction="row" gap="0.5rem" alignItems="center" flexWrap="wrap">
              <RenderWithCondition
                condition={!!documentChange.before}
                fallBack={<Typography color="error">No data</Typography>}
              >
                <NamedEntityList
                  items={[{ id: documentChange.before?.documentId!, name: documentChange.before?.docName! }]}
                />
              </RenderWithCondition>

              <ArrowRightAlt />

              <RenderWithCondition
                condition={!!documentChange.after}
                fallBack={<Typography color="error">Removed</Typography>}
              >
                <NamedEntityList
                  items={[{ id: documentChange.after?.documentId!, name: documentChange.after?.docName! }]}
                />
              </RenderWithCondition>
            </Stack>
          </Stack>
        );
      }

      const scalarChange = change as ScalarChange<string>;

      return (
        <Stack direction="row" gap="0.5rem" alignItems="center" flexWrap="wrap">
          <Typography>{label}:</Typography>

          <>
            {!scalarChange.before || isEqual(scalarChange.before, EMPTY_QUILL) ? (
              <Typography color="error">No data</Typography>
            ) : (
              <ReadOnlyQuill value={scalarChange.before ?? '—'} />
            )}
          </>

          <ArrowRightAlt />

          <>
            {!scalarChange.after || isEqual(scalarChange.after, EMPTY_QUILL) ? (
              <Typography color="error">No data</Typography>
            ) : (
              <ReadOnlyQuill value={scalarChange.after ?? '—'} />
            )}
          </>
        </Stack>
      );
    })}
  </Stack>
);
